import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { Block, HiVis } from "@/types/editor/blocks";
import { updateHiVisDesign, cloneHiVis } from "@/lib/hivis-manager";
import { VisEditorProps } from "./vis-editor/types";
import {
  calculateRect,
  getHandles,
  getSelectedBlockBounds,
  getNaturalWidthRatio,
  convertToCanvasCoords,
  findClickedBlock,
} from "./vis-editor/utils";
import {
  renderBlock,
  renderWithOverlay,
  renderFloatingBlocks,
  RenderContext,
} from "./vis-editor/renderer";
import { createInteractionHandlers } from "./vis-editor/interactions";

const VisEditor = ({
  canvasRef,
  hiVis,
  onHiVisChange,
  enableTransform = true,
  selectedPath = [],
  onSelect,
  debugMode = false,
  showPattern = false,
  externalInputRef,
}: VisEditorProps) => {
  const SCALE = 3.779528; // 1mm = 3.779528 pixels (96 DPI)
  const BASE_HANDLE_SIZE = 8;
  const MOBILE_HANDLE_SIZE = 16;
  const PIXEL_RATIO =
    typeof window !== "undefined"
      ? Math.min(window.devicePixelRatio || 1, 2)
      : 1;

  // Pattern related refs and state
  const patternSvgRef = useRef<HTMLImageElement | null>(null);
  const [patternLoaded, setPatternLoaded] = useState(false);
  const [patternColor, setPatternColor] = useState("#F3FF00"); // Default yellow pattern

  // Extract all properties from hiVis in a consistent way
  // Design properties
  const design = hiVis?.design;
  const blocks = useMemo(() => design?.blocks || [], [design?.blocks]);
  const rootBlock = blocks[0];
  const width = rootBlock?.width || 0;
  const height = rootBlock?.height || 0;
  const global_gap = design?.global_gap || 0;

  // Overlay and background image properties
  const overlayImageUrl = design?.overlayImage || "";
  const overlayWidth = design?.overlayWidth || 0;
  const overlayHeight = design?.overlayHeight || 0;

  const backgroundImageUrl = design?.backgroundImage || "";
  const backgroundWidth = design?.backgroundWidth || 0;
  const backgroundHeight = design?.backgroundHeight || 0;

  // Inset properties
  const insetWidth = design?.insetWidth || 0;
  const insetHeight = design?.insetHeight || 0;
  const insetX = design?.insetX || 0;
  const insetY = design?.insetY || 0;

  // Character properties
  const characterWidth = design?.characterWidth || 0;
  const characterHeight = design?.characterHeight || 0;

  // HiVis specific properties
  const gap = hiVis?.gap || 0;
  const naturalWidth = hiVis?.naturalWidth || false;
  const baseMaterial = useMemo(
    () => ({
      color: design?.blocks?.[0]?.color || "#FFFFFF",
    }),
    [design?.blocks]
  ); // Use first block's color instead of always white

  // Add RAF and throttle references
  const rafRef = useRef<number>();
  const throttleRef = useRef<NodeJS.Timeout>();
  const containerRef = useRef<HTMLDivElement>(null);

  // Add ref for temporary overlay rendering
  const tempOverlayRef = useRef<HTMLImageElement | null>(null);
  // Add ref for temporary background rendering
  const tempBackgroundRef = useRef<HTMLImageElement | null>(null);

  // Add image loading state
  const [overlayImage, setOverlayImage] = useState<HTMLImageElement | null>(
    null
  );
  // Add background image loading state
  const [backgroundImageState, setBackgroundImageState] =
    useState<HTMLImageElement | null>(null);

  // Add image loading state
  const [imagesLoading, setImagesLoading] = useState({
    overlay: false,
    background: false,
  });

  // State
  const [containerSize, setContainerSize] = useState(0);
  const [isSelected, setIsSelected] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [activeHandle, setActiveHandle] = useState<string | null>(null);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [screenWidth, setScreenWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1920
  );
  const [rectBounds, setRectBounds] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  // Add state for optimizing renders
  const [isInteracting, setIsInteracting] = useState(false);
  const [hasUpdated, setHasUpdated] = useState(true); // Start true to render initially

  // Use a ref to track previous hiVis value to detect changes properly
  const prevHiVisRef = useRef<HiVis | null>(null);

  // Derived values
  const HANDLE_SIZE = isMobile ? MOBILE_HANDLE_SIZE : BASE_HANDLE_SIZE;

  // Get values from hiVis prop
  const useNaturalWidth = naturalWidth;

  // Calculate padding based on screen width
  const PADDING = useMemo(() => {
    if (screenWidth < 640) return 35; // Mobile
    if (screenWidth < 768) return 35; // Small tablets
    if (screenWidth < 1024) return 35; // Tablets
    if (screenWidth < 1280) return 38; // Small laptops
    return 40; // Desktop
  }, [screenWidth]);

  // Load overlay image when URL changes
  useEffect(() => {
    let isMounted = true;
    let loadingTimeoutId: NodeJS.Timeout | null = null;

    if (overlayImageUrl) {
      // Delay showing the loading indicator to prevent flashing for cached images
      loadingTimeoutId = setTimeout(() => {
        if (isMounted) {
          setImagesLoading((prev) => ({ ...prev, overlay: true }));
        }
      }, 50); // 50ms delay before showing spinner

      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        if (isMounted) {
          // Clear the loading timeout if it hasn't fired yet
          if (loadingTimeoutId) clearTimeout(loadingTimeoutId);
          setOverlayImage(img);
          setImagesLoading((prev) => ({ ...prev, overlay: false }));
          setHasUpdated(true);
        }
      };
      img.onerror = () => {
        if (isMounted) {
          // Clear the loading timeout if it hasn't fired yet
          if (loadingTimeoutId) clearTimeout(loadingTimeoutId);
          setOverlayImage(null);
          setImagesLoading((prev) => ({ ...prev, overlay: false }));
          console.error("Failed to load overlay image:", overlayImageUrl);
        }
      };
      img.src = overlayImageUrl;
    } else {
      setOverlayImage(null);
      setImagesLoading((prev) => ({ ...prev, overlay: false }));
    }

    return () => {
      isMounted = false;
      if (loadingTimeoutId) clearTimeout(loadingTimeoutId);
    };
  }, [overlayImageUrl]);

  // Load background image when URL changes
  useEffect(() => {
    let isMounted = true;
    let loadingTimeoutId: NodeJS.Timeout | null = null;

    if (backgroundImageUrl) {
      // Delay showing the loading indicator to prevent flashing for cached images
      loadingTimeoutId = setTimeout(() => {
        if (isMounted) {
          setImagesLoading((prev) => ({ ...prev, background: true }));
        }
      }, 50); // 50ms delay before showing spinner

      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        if (isMounted) {
          // Clear the loading timeout if it hasn't fired yet
          if (loadingTimeoutId) clearTimeout(loadingTimeoutId);
          setBackgroundImageState(img);
          setImagesLoading((prev) => ({ ...prev, background: false }));
          setHasUpdated(true);
        }
      };
      img.onerror = () => {
        if (isMounted) {
          // Clear the loading timeout if it hasn't fired yet
          if (loadingTimeoutId) clearTimeout(loadingTimeoutId);
          setBackgroundImageState(null);
          setImagesLoading((prev) => ({ ...prev, background: false }));
          console.error("Failed to load background image:", backgroundImageUrl);
        }
      };
      img.src = backgroundImageUrl;
    } else {
      setBackgroundImageState(null);
      setImagesLoading((prev) => ({ ...prev, background: false }));
    }

    return () => {
      isMounted = false;
      if (loadingTimeoutId) clearTimeout(loadingTimeoutId);
    };
  }, [backgroundImageUrl]);

  // Force re-render when pattern loads
  useEffect(() => {
    if (patternLoaded) {
      setHasUpdated(true);
    }
  }, [patternLoaded]);

  // Update pattern color when base material changes
  useEffect(() => {
    // If the pattern is already loaded and base material changes, we need to reload the pattern with new color
    if (baseMaterial && patternSvgRef.current) {
      let newPatternFill = "#F3FF00"; // Default yellow

      if (
        baseMaterial.color === "#E8E8E8" ||
        baseMaterial.color.toLowerCase().includes("white")
      ) {
        newPatternFill = "#FFFFFF"; // White for white base material
      } else if (
        baseMaterial.color === "#AF0000" ||
        baseMaterial.color.toLowerCase().includes("red")
      ) {
        newPatternFill = "#FF0000"; // Red for red base material
      }

      // Only update if color actually changed
      if (newPatternFill !== patternColor) {
        setPatternColor(newPatternFill);
        setPatternLoaded(false); // Force reload of pattern
      }
    }
  }, [baseMaterial, patternColor]);

  // Check if device is mobile and update screen width
  useEffect(() => {
    const checkMobileAndWidth = () => {
      setIsMobile(window.matchMedia("(max-width: 768px)").matches);
      setScreenWidth(window.innerWidth);
    };

    checkMobileAndWidth();
    window.addEventListener("resize", checkMobileAndWidth);
    return () => window.removeEventListener("resize", checkMobileAndWidth);
  }, []);

  // Update container size on mount and resize
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const width = containerRef.current.clientWidth;
        setContainerSize(width);
      }
    };

    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  // Create and load SVG pattern with color updates
  useEffect(() => {
    let blobUrl: string | null = null;
    let isActive = true;

    const createAndLoadPattern = () => {
      // Cleanup previous blob URL if it exists
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        blobUrl = null;
      }

      // Determine pattern color based on base material
      let newPatternFill = "#F3FF00"; // Default yellow

      if (baseMaterial) {
        if (
          baseMaterial.color === "#E8E8E8" ||
          baseMaterial.color.toLowerCase().includes("white")
        ) {
          newPatternFill = "#FFFFFF"; // White for white base material
        } else if (
          baseMaterial.color === "#AF0000" ||
          baseMaterial.color.toLowerCase().includes("red")
        ) {
          newPatternFill = "#FF0000"; // Red for red base material
        }
      }

      // Only update if color actually changed
      if (newPatternFill !== patternColor) {
        setPatternColor(newPatternFill);
      }

      // Create SVG image for pattern
      const img = new Image();
      let localBlobUrl: string | null = null;

      // Create SVG string
      const svgString = `
        <svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 22.3 12.1">
          <defs>
            <style>
              .st0 { fill: ${newPatternFill}; }
            </style>
          </defs>
          <path class="st0" d="M22.3,3.2V0h-2.8c.6.1,1,.3,1.2.4.2.3.9,1.5,1.6,2.8ZM9,4.5c-.8-.2-1.7-.3-2.6-.3s-1.8.1-2.6.3c-.4,0-.7.2-1,.3C2.1,3.6.6,1,0,0v3.2c.6,1.1,1.3,2.2,1.7,2.8-.3.6-1,1.8-1.7,2.9v3.2c.7-1.3,2.9-5,3.3-5.6.3-.2,1.7-.7,3.1-.7s2.9.4,3.1.7c.3.6,2.5,4.4,3.3,5.6h2.8c.6-.1,1.2-.2,2-.2s1.4,0,2,.2h2.8v-3.2c-.4.8-.9,1.5-1.2,2-.3-.1-.6-.2-1-.3-.8-.2-1.7-.3-2.6-.3s-1.8.1-2.6.3c-.4,0-.7.2-1,.3-.8-1.3-2.2-3.9-2.8-4.9.7-1.3,2.9-5,3.3-5.6.1-.1.6-.3,1.2-.4h-2.8c-.6,1-2.1,3.6-2.8,4.9-.3-.1-.6-.2-1-.3Z"/>
        </svg>
      `;

      try {
        // Convert SVG string to blob URL
        const blob = new Blob([svgString], { type: "image/svg+xml" });
        localBlobUrl = URL.createObjectURL(blob);

        // Set up image load handlers before setting src
        img.onload = () => {
          if (isActive) {
            patternSvgRef.current = img;
            setPatternLoaded(true);
            // Only store the blob URL after successful load
            blobUrl = localBlobUrl;
          } else {
            // Clean up if component is no longer active
            if (localBlobUrl) {
              URL.revokeObjectURL(localBlobUrl);
            }
          }
        };

        img.onerror = () => {
          console.error("Failed to load pattern SVG");
          if (isActive) {
            patternSvgRef.current = null;
            setPatternLoaded(false);
          }
          // Clean up on error
          if (localBlobUrl) {
            URL.revokeObjectURL(localBlobUrl);
          }
        };

        // Set src after handlers are set up
        img.src = localBlobUrl;
      } catch (error) {
        console.error("Error creating pattern SVG:", error);
        if (isActive) {
          patternSvgRef.current = null;
          setPatternLoaded(false);
        }
        // Clean up on error
        if (localBlobUrl) {
          URL.revokeObjectURL(localBlobUrl);
        }
      }
    };

    // Initial creation
    createAndLoadPattern();

    return () => {
      isActive = false;
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        blobUrl = null;
      }
      patternSvgRef.current = null;
      setPatternLoaded(false);
    };
  }, [baseMaterial, patternColor]);

  // Set up high DPI canvas with proper sizing
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set display size
    canvas.style.width = containerSize + "px";
    canvas.style.height = containerSize + "px";

    // Set actual size in memory
    canvas.width = containerSize * PIXEL_RATIO;
    canvas.height = containerSize * PIXEL_RATIO;

    // Scale all drawing operations by the pixel ratio
    ctx.scale(PIXEL_RATIO, PIXEL_RATIO);
  }, [PIXEL_RATIO, containerSize, canvasRef]);

  // Get transform handles positions

  // Add function to calculate natural width ratio

  // Helper function to render pattern

  // Update renderBlock to handle natural widths and vertical stacking

  // Add refs for dimension tracking
  const prevWidth = useRef<number>(0);
  const prevHeight = useRef<number>(0);

  // Add ref for dimension updates
  const pendingUpdateRef = useRef<{
    width?: number;
    height?: number;
    timestamp: number;
    isProcessing?: boolean;
  } | null>(null);
  const animationFrameRef = useRef<number>();
  const renderTimeoutRef = useRef<number>();
  const drawRef = useRef<() => void>();

  // Force re-render when pattern loads or dimensions change
  useEffect(() => {
    if (
      patternLoaded ||
      rootBlock?.width !== prevWidth.current ||
      rootBlock?.height !== prevHeight.current
    ) {
      setHasUpdated(true);
      prevWidth.current = rootBlock?.width || 0;
      prevHeight.current = rootBlock?.height || 0;
    }
  }, [patternLoaded, rootBlock?.width, rootBlock?.height]);

  const calculateRectImpl = useCallback(() => {
    return calculateRect(
      width,
      height,
      containerSize,
      PADDING,
      SCALE,
      insetWidth,
      insetHeight,
      insetX,
      insetY,
      overlayImageUrl,
      overlayWidth,
      overlayHeight,
      backgroundImageUrl,
      backgroundWidth,
      backgroundHeight,
      characterWidth,
      characterHeight
    );
  }, [
    width,
    height,
    containerSize,
    PADDING,
    SCALE,
    insetWidth,
    insetHeight,
    insetX,
    insetY,
    overlayImageUrl,
    overlayWidth,
    overlayHeight,
    backgroundImageUrl,
    backgroundWidth,
    backgroundHeight,
    characterWidth,
    characterHeight,
  ]);

  // Function to get the selected block's bounds
  const getSelectedBlockBoundsImpl = useCallback(() => {
    return getSelectedBlockBounds(selectedPath, blocks, () =>
      calculateRectImpl()
    );
  }, [selectedPath, blocks, calculateRectImpl]);

  // Implement calculateRect with all required parameters

  // Create interaction handlers
  const {
    handleStart,
    handleEnd,
    handleMouseDown,
    throttledMove,
    handleCanvasClick,
  } = createInteractionHandlers({
    calculateRect: calculateRectImpl,
    getHandles,
    convertToCanvasCoords: (x, y) =>
      convertToCanvasCoords(x, y, canvasRef, containerSize),
    getSelectedBlockBounds: getSelectedBlockBoundsImpl,
    findClickedBlock: (x, y, block) =>
      findClickedBlock(
        x,
        y,
        block,
        calculateRectImpl,
        getSelectedBlockBoundsImpl,
        [],
        null
      ),
    canvasRef,
    throttleRef,
    pendingUpdateRef,
    animationFrameRef,
    renderTimeoutRef,
    drawRef,
    blocks,
    width,
    height,
    containerSize,
    SCALE,
    HANDLE_SIZE,
    MOBILE_HANDLE_SIZE,
    isSelected,
    isDragging,
    activeHandle,
    startPos,
    isMobile,
    rectBounds,
    selectedPath,
    enableTransform,
    hiVis,
    setIsSelected,
    setIsDragging,
    setActiveHandle,
    setStartPos,
    setIsInteracting,
    setHasUpdated,
    onHiVisChange,
    onSelect,
  });

  // Create a ref for the render function
  const renderRef = useRef<() => void>();

  // Update renderBlock to handle natural widths and vertical stacking
  const renderBlockImpl = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      block: Block,
      x: number,
      y: number,
      width: number,
      height: number
    ) => {
      const context: RenderContext = {
        patternSvgRef: patternSvgRef.current,
        rootBlock: rootBlock || block,
        SCALE,
        patternLoaded,
        debugMode,
        calculateRect: calculateRectImpl,
        baseMaterial,
        useNaturalWidth: useNaturalWidth,
        getNaturalWidthRatio,
        characterWidth,
        characterHeight,
        gap,
        global_gap,
        width,
        height,
        showPattern,
      };

      renderBlock(ctx, block, x, y, width, height, context);
    },
    [
      patternSvgRef,
      rootBlock,
      SCALE,
      patternLoaded,
      debugMode,
      calculateRectImpl,
      baseMaterial,
      useNaturalWidth,
      characterWidth,
      characterHeight,
      gap,
      global_gap,
      showPattern,
    ]
  );

  // Update the render function to enhance performance and prevent flickering
  const render = useCallback(() => {
    // Only run animation frame if we're interacting, hiVis was updated, or we're exporting
    // This optimization prevents continuous rerenders when nothing is changing
    if (!isInteracting && !hasUpdated && !canvasRef.current?.isExporting) {
      return;
    }

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = undefined;
    }

    rafRef.current = requestAnimationFrame(() => {
      // Set a flag to prevent multiple renders during the same frame
      if (rafRef.current === null) return;

      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // Reset the scale before clearing
      ctx.setTransform(1, 0, 0, 1, 0, 0);

      // Only clear the canvas if we're not in the middle of a dimension change
      // to prevent flickering during resizing
      if (!pendingUpdateRef.current?.isProcessing) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }

      // Set the scale for high DPI
      ctx.setTransform(PIXEL_RATIO, 0, 0, PIXEL_RATIO, 0, 0);

      // Calculate rectangle bounds
      const rect = calculateRectImpl();
      setRectBounds(rect);

      // Draw the background image if it exists (either temporary or regular)
      const backgroundToUse = tempBackgroundRef.current || backgroundImageState;
      if (backgroundToUse) {
        const availableSpace = containerSize - PADDING * 2;
        const x = PADDING;
        const y = PADDING;

        // Save context before drawing
        ctx.save();

        // Draw the background image scaled to fit the available space
        ctx.drawImage(backgroundToUse, x, y, availableSpace, availableSpace);

        if (debugMode) {
          // Draw a border around the inset area for debugging
          const insetRatioX = insetX / backgroundWidth;
          const insetRatioY = insetY / backgroundHeight;
          const insetRatioWidth = insetWidth / backgroundWidth;
          const insetRatioHeight = insetHeight / backgroundHeight;

          const scaledInsetX = x + availableSpace * insetRatioX;
          const scaledInsetY = y + availableSpace * insetRatioY;
          const scaledInsetWidth = availableSpace * insetRatioWidth;
          const scaledInsetHeight = availableSpace * insetRatioHeight;

          ctx.strokeStyle = "rgba(0, 0, 255, 0.5)"; // Blue for background
          ctx.lineWidth = 2;
          ctx.strokeRect(
            scaledInsetX,
            scaledInsetY,
            scaledInsetWidth,
            scaledInsetHeight
          );
        }

        // Restore context after drawing
        ctx.restore();
      }

      // Draw the overlay image if it exists (either temporary or regular)
      const overlayToUse = tempOverlayRef.current || overlayImage;
      if (overlayToUse) {
        const availableSpace = containerSize - PADDING * 2;
        const x = PADDING;
        const y = PADDING;

        // Save context before drawing
        ctx.save();

        // Draw the overlay image scaled to fit the available space
        ctx.drawImage(overlayToUse, x, y, availableSpace, availableSpace);

        if (debugMode) {
          // Draw a border around the inset area for debugging
          const insetRatioX = insetX / overlayWidth;
          const insetRatioY = insetY / overlayHeight;
          const insetRatioWidth = insetWidth / overlayWidth;
          const insetRatioHeight = insetHeight / overlayHeight;

          const scaledInsetX = x + availableSpace * insetRatioX;
          const scaledInsetY = y + availableSpace * insetRatioY;
          const scaledInsetWidth = availableSpace * insetRatioWidth;
          const scaledInsetHeight = availableSpace * insetRatioHeight;

          ctx.strokeStyle = "rgba(255, 0, 0, 0.5)"; // Red for overlay
          ctx.lineWidth = 2;
          ctx.strokeRect(
            scaledInsetX,
            scaledInsetY,
            scaledInsetWidth,
            scaledInsetHeight
          );
        }

        // Restore context after drawing
        ctx.restore();
      }

      // Draw blocks if available, otherwise draw single block
      if (blocks && blocks.length > 0) {
        blocks.forEach((block, index) => {
          const blockToRender =
            index === 0 && selectedPath.length === 0
              ? { ...block, width: width, height: height }
              : block;

          // Wrap the block rendering in save/restore for isolation
          ctx.save();

          renderBlockImpl(
            ctx,
            blockToRender,
            rect.x,
            rect.y,
            rect.width,
            rect.height
          );

          ctx.restore();
        });

        // Render floating blocks on top of main blocks
        if (
          hiVis?.design?.floating_blocks &&
          hiVis.design.floating_blocks.length > 0
        ) {
          ctx.save();

          renderFloatingBlocks(
            ctx,
            hiVis.design.floating_blocks,
            rect.x,
            rect.y,
            rect.width,
            rect.height,
            {
              patternSvgRef: patternSvgRef.current,
              rootBlock: rootBlock || blocks[0],
              SCALE,
              patternLoaded,
              debugMode,
              calculateRect: calculateRectImpl,
              baseMaterial,
              useNaturalWidth: useNaturalWidth,
              getNaturalWidthRatio,
              characterWidth,
              characterHeight,
              gap,
              global_gap,
              width,
              height,
              showPattern,
            }
          );

          ctx.restore();
        }
      } else {
        ctx.save();

        renderBlockImpl(
          ctx,
          {
            width: rect.width,
            height: rect.height,
            character: "",
          },
          rect.x,
          rect.y,
          rect.width,
          rect.height
        );

        ctx.restore();
      }

      // Draw selection handles and measurements if selected
      if (isSelected && enableTransform) {
        ctx.save();

        const selectedRect = getSelectedBlockBoundsImpl();
        const handles = getHandles(selectedRect);

        // Draw connecting lines
        ctx.beginPath();
        ctx.strokeStyle = "#0066ff";
        ctx.lineWidth = 2;

        // Connect the corner handles
        const cornerHandles = handles.slice(4);
        ctx.moveTo(cornerHandles[0].x, cornerHandles[0].y);
        ctx.lineTo(cornerHandles[1].x, cornerHandles[1].y);
        ctx.lineTo(cornerHandles[3].x, cornerHandles[3].y);
        ctx.lineTo(cornerHandles[2].x, cornerHandles[2].y);
        ctx.lineTo(cornerHandles[0].x, cornerHandles[0].y);
        ctx.stroke();

        // Draw handles
        handles.forEach((handle) => {
          const isActive = activeHandle === handle.action;
          const handleSize = isMobile ? MOBILE_HANDLE_SIZE : BASE_HANDLE_SIZE;
          const drawSize = isActive ? handleSize + 4 : handleSize;

          ctx.fillStyle = isActive ? "#66a3ff" : "#0066ff";
          ctx.fillRect(
            handle.x - drawSize / 2,
            handle.y - drawSize / 2,
            drawSize,
            drawSize
          );
        });

        // Draw measurements
        const fontSize = isMobile ? 20 : 14;
        ctx.font = `${fontSize}px Arial`;
        ctx.fillStyle = "#0066ff";

        // Get the actual dimensions of the selected block
        const selectedBlock =
          selectedPath && selectedPath.length > 0
            ? (() => {
                let block = blocks?.[selectedPath[0]];
                for (let i = 1; i < selectedPath.length; i++) {
                  if (!block?.blocks) break;
                  block = block.blocks[selectedPath[i]];
                }
                return block;
              })()
            : blocks?.[0] || null;

        // Display width and height
        const displayWidth = selectedBlock?.width || width;
        const displayHeight = selectedBlock?.height || height;
        const widthText = `${displayWidth}mm`;
        const heightText = `${displayHeight}mm`;

        // Position text
        const widthTextX = selectedRect.x + selectedRect.width / 2;
        const widthTextY = selectedRect.y - 10;
        const heightTextX = selectedRect.x - 10;
        const heightTextY = selectedRect.y + selectedRect.height / 2;

        // Draw width text
        ctx.save();
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText(widthText, widthTextX, widthTextY);
        ctx.restore();

        // Draw height text
        ctx.save();
        ctx.textAlign = "right";
        ctx.textBaseline = "middle";
        ctx.translate(heightTextX, heightTextY);
        ctx.rotate(-Math.PI / 2);
        ctx.fillText(heightText, 0, 0);
        ctx.restore();

        ctx.restore();
      }

      // Clear the animation frame reference when done if not interacting
      if (!isInteracting && !canvas.isExporting) {
        rafRef.current = undefined;
      }

      // After rendering, reset the hasUpdated flag
      setHasUpdated(false);
    });
  }, [
    calculateRectImpl,
    blocks,
    width,
    height,
    renderBlockImpl,
    isSelected,
    enableTransform,
    activeHandle,
    isMobile,
    BASE_HANDLE_SIZE,
    MOBILE_HANDLE_SIZE,
    selectedPath,
    getSelectedBlockBoundsImpl,
    PIXEL_RATIO,
    canvasRef,
    containerSize,
    PADDING,
    overlayImage,
    backgroundImageState,
    tempOverlayRef,
    tempBackgroundRef,
    debugMode,
    insetX,
    insetY,
    insetWidth,
    insetHeight,
    backgroundWidth,
    backgroundHeight,
    overlayWidth,
    overlayHeight,
    isInteracting,
    hasUpdated,
    pendingUpdateRef,
    hiVis,
    baseMaterial,
    characterHeight,
    characterWidth,
    gap,
    global_gap,
    patternLoaded,
    rootBlock,
    showPattern,
    useNaturalWidth,
  ]);

  // Store render function in ref to avoid circular references
  useEffect(() => {
    renderRef.current = render;
  }, [render]);

  // Add method to handle temporary overlay rendering
  const renderWithOverlayImpl = useCallback(() => {
    return renderWithOverlay({
      canvasRef,
      overlayImageUrl,
      backgroundImageUrl,
      setImagesLoading,
      tempOverlayRef,
      tempBackgroundRef,
      setHasUpdated,
      render: () => renderRef.current?.(),
    });
  }, [
    canvasRef,
    overlayImageUrl,
    backgroundImageUrl,
    setImagesLoading,
    tempOverlayRef,
    tempBackgroundRef,
    setHasUpdated,
  ]);

  // Expose the renderWithOverlay method via ref
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.renderWithOverlay = renderWithOverlayImpl;
    }

    return () => {
      if (canvas) {
        canvas.renderWithOverlay = undefined;
      }
    };
  }, [canvasRef, renderWithOverlayImpl]);

  // Store draw function in ref
  useEffect(() => {
    drawRef.current = render;
  }, [render]);

  // Add an effect to trigger render when needed
  useEffect(() => {
    // Only render if no updates are being processing
    if (!pendingUpdateRef.current?.isProcessing) {
      // Force a render on component mount
      drawRef.current?.();
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add an effect to start the render loop when isInteracting or hasUpdated changes
  useEffect(() => {
    if ((isInteracting || hasUpdated) && !rafRef.current) {
      drawRef.current?.();
    }
  }, [isInteracting, hasUpdated, rafRef]);

  // Clean up animation frame on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [rafRef]);

  // Handle both mouse and touch events

  // Mouse event handlers

  // Throttle move handler with increased threshold for mobile

  // Setup mouse event handlers on window
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging || isSelected) {
        throttledMove(e.clientX, e.clientY);
      }
    };

    const handleMouseUp = () => handleEnd();

    if (isDragging || isSelected) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, isSelected, throttledMove, handleEnd]);

  // Setup touch events with non-passive listeners
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const touchStartHandler = (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.touches[0];
      handleStart(touch.clientX, touch.clientY);
    };

    const touchMoveHandler = (e: TouchEvent) => {
      e.preventDefault();
      if (isDragging || isSelected) {
        const touch = e.touches[0];
        throttledMove(touch.clientX, touch.clientY);
      }
    };

    const touchEndHandler = () => handleEnd();

    canvas.addEventListener("touchstart", touchStartHandler, {
      passive: false,
    });
    canvas.addEventListener("touchmove", touchMoveHandler, { passive: false });
    canvas.addEventListener("touchend", touchEndHandler);
    canvas.addEventListener("touchcancel", touchEndHandler);

    return () => {
      canvas.removeEventListener("touchstart", touchStartHandler);
      canvas.removeEventListener("touchmove", touchMoveHandler);
      canvas.removeEventListener("touchend", touchEndHandler);
      canvas.removeEventListener("touchcancel", touchEndHandler);
    };
  }, [
    handleStart,
    handleEnd,
    throttledMove,
    isDragging,
    isSelected,
    canvasRef,
  ]);

  // Clean up RAF and throttle on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      if (throttleRef.current) {
        clearTimeout(throttleRef.current);
      }
    };
  }, []);

  // Handle canvas click

  useEffect(() => {
    if (!canvasRef.current || !hiVis) return;

    // Reset transform state when hiVis changes
    setIsDragging(false);
    setActiveHandle(null);
    setStartPos({ x: 0, y: 0 });
    setIsSelected(false);

    // Force a redraw
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      drawRef.current?.();
    }
  }, [
    // Use stringified values to prevent object reference issues
    hiVis?.assetNumber,
    hiVis?.stackFirstTwo,
    hiVis?.naturalWidth,
    hiVis?.design?.id,
    containerSize,
    hiVis,
    canvasRef,
    setIsDragging,
    setActiveHandle,
    setStartPos,
    setIsSelected,
    drawRef,
  ]);

  // Improve the effect to detect when hiVis changes and force rerender
  useEffect(() => {
    if (hiVis && prevHiVisRef.current) {
      // When design changes completely, force a full redraw
      if (hiVis.design?.id !== prevHiVisRef.current.design?.id) {
        // Force redraw when design changes
        setTimeout(() => {
          setHasUpdated(true);
          drawRef.current?.();
        }, 50);
      }
    }

    // Always store current hiVis and trigger render
    prevHiVisRef.current = hiVis ? cloneHiVis(hiVis) : null;

    // Force a render when hiVis changes
    setHasUpdated(true);

    // Schedule a render after state updates have settled
    const timeoutId = setTimeout(() => {
      drawRef.current?.();
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [hiVis, setHasUpdated]);

  // Add a more robust mount effect to ensure proper initialization
  useEffect(() => {
    // Set a flag to track if the component is mounted
    let isMounted = true;

    // Initialize immediately to avoid blank canvas on first render
    if (canvasRef.current && drawRef.current) {
      drawRef.current();
    }

    // Create a function to handle progressive retries with increasing intervals
    const scheduleProgressiveRetries = () => {
      // Schedule initial renders at increasing intervals up to 3 seconds
      // This gives images more time to load on slower connections
      const intervals = [100, 250, 500, 1000, 1500, 2000, 3000];

      return intervals.map((delay) =>
        setTimeout(() => {
          if (!isMounted) return;

          // Force a hasUpdated to true to ensure render happens
          setHasUpdated(true);

          // Invoke the draw function if available
          if (drawRef.current) {
            drawRef.current();
          }
        }, delay)
      );
    };

    // Schedule the progressive retries
    const timeoutIds = scheduleProgressiveRetries();

    // Clean up timeouts if component unmounts
    return () => {
      isMounted = false;
      timeoutIds.forEach((id) => clearTimeout(id));
    };
  }, [canvasRef, setHasUpdated]);

  // Add an additional effect to monitor any changes to the hiVis object and force render
  useEffect(() => {
    if (!hiVis) return;

    // Set hasUpdated to trigger a render cycle
    setHasUpdated(true);

    // Add a small delay to ensure state updates have propagated
    const timeoutId = setTimeout(() => {
      drawRef.current?.();
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [hiVis, setHasUpdated]);

  // Throttle render calls during slider operations
  const throttledRender = useCallback(() => {
    // Only throttle if we're already throttling
    if (throttleRef.current) {
      return;
    }

    // Perform the render operation
    render();

    // Set a new throttle to prevent too many renders
    throttleRef.current = setTimeout(() => {
      throttleRef.current = undefined;
    }, 32); // ~30fps
  }, [render, throttleRef]);

  // Store throttledRender in ref for access in other callbacks
  useEffect(() => {
    drawRef.current = throttledRender;
  }, [throttledRender]);

  // Add an effect to ensure render happens when images are loaded
  useEffect(() => {
    // Force a render when overlay or background image changes
    if (overlayImage || backgroundImageState) {
      setHasUpdated(true);

      // Schedule multiple renders with progressively longer delays
      // This helps ensure images are fully rendered
      const timeoutIds = [10, 50, 100, 250, 500, 1000].map((delay) =>
        setTimeout(() => {
          if (drawRef.current) drawRef.current();
        }, delay)
      );

      return () => timeoutIds.forEach((id) => clearTimeout(id));
    }
  }, [overlayImage, backgroundImageState]);

  // Add this inside the VisEditor component where hiVis changes are handled
  useEffect(() => {
    if (
      hiVis &&
      prevHiVisRef.current &&
      (hiVis.assetNumber !== prevHiVisRef.current.assetNumber ||
        hiVis.stackFirstTwo !== prevHiVisRef.current.stackFirstTwo ||
        hiVis.naturalWidth !== prevHiVisRef.current.naturalWidth)
    ) {
      // Asset number changed, update the design accordingly
      // Create a deep clone to prevent any reference issues
      const hiVisCopy = cloneHiVis(hiVis);
      const updatedHiVis = updateHiVisDesign(hiVisCopy);

      if (onHiVisChange) {
        onHiVisChange(updatedHiVis);
      }
    }
  }, [hiVis, onHiVisChange]);

  // Force a redraw when containerSize or screenWidth changes (fixes blank canvas on resize)
  useEffect(() => {
    setHasUpdated(true);
    drawRef.current?.();
  }, [containerSize, screenWidth]);

  const handleCanvasClickForInput = () => {
    if (isMobile && externalInputRef?.current) {
      externalInputRef.current.focus();
    }
  };

  return (
    <div className='relative w-full'>
      <div
        ref={containerRef}
        className='relative bg-gradient-to-b from-[#404040] to-[#262626] aspect-square w-full'
      >
        {(imagesLoading.overlay || imagesLoading.background) && (
          <div className='absolute inset-0 flex items-center justify-center bg-black/30 z-10 rounded-lg'>
            <div className='h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent'></div>
          </div>
        )}
        <canvas
          ref={canvasRef}
          className='absolute inset-0 w-full h-full border rounded-lg touch-none vis-editor-main'
          style={{ touchAction: "none" }}
          onMouseDown={handleMouseDown}
          onClick={(e) => {
            handleCanvasClick(e);
            handleCanvasClickForInput();
          }}
        />
      </div>
    </div>
  );
};

export default VisEditor;
